import {computed, defineComponent, onMounted, ref} from "vue";
import * as KlipApi from "@/api/klip-api.proxy";
import {useForm} from 'vee-validate';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import {first, isEmpty} from 'lodash-es';
import {useKlModalEventsUtil} from '@/app/shared/helpers/kl-modal-events-util';
import DateUtil from '@/app/shared/helpers/date-util';
import TemplateHelper from '@/app/shared/helpers/template-helper';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import DOMPurify from "dompurify";

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlAdminVersions',
    setup() {

        const schema = yup.object({
            activationDate: yup.array().kl_required_date().label('Activatie datum'),
            klipPackage: yup.string().required().label('KLIP Package'),
            pmklVersion: yup.string().required().label('PMKL'),
            wfsVersion: yup.string().required().label('WFS'),
            minViewer: yup.string().required().label('Minimum Viewer'),
            viewer: yup.string().required().label('Viewer'),
            minKlipPackage: yup.string().required().label('Minimum KLIP Package'),
            releaseNotes: yup.string().required().label('Release Notes'),
        });

        const modalEvents = useKlModalEventsUtil('new-version-modal', {
            onClosed() {
                savingVersions.value = false;
            },
        });

        const form = useForm({
            validationSchema: toTypedSchema(schema),
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const activationDate = fields.activationDate.getValue<string[]>();
        const klipPackage = fields.klipPackage.getValue<string>();
        const pmklVersion = fields.pmklVersion.getValue<string>();
        const wfsVersion = fields.wfsVersion.getValue<string>();
        const minViewer = fields.minViewer.getValue<string>();
        const viewer = fields.viewer.getValue<string>();
        const minKlipPackage = fields.minKlipPackage.getValue<string>();
        const releaseNotes = fields.releaseNotes.getValue<string>();

        const proxy = useKlipApiProxy();
        const today = DateUtil.formatDate(DateUtil.now());

        const activationMinDate = ref<string>(today);
        const savingVersions = ref<boolean>(false);
        const isVersionsDataTableLoading = ref<boolean>(true);
        const allVersions = ref<KlipApi.IVersionDto[]>([]);
        const showAllVersions = ref<boolean>(false);
        const versionsMeta = ref<{ totalRows: number, resultsPerPage: number, currentPage: number | undefined }>({ totalRows: 1, resultsPerPage: 1, currentPage: 1 });


        const versions = computed((): KlipApi.IVersionDto[] => {
            if (isEmpty(allVersions.value)) {
                return [];
            }
            return showAllVersions.value ? allVersions.value : [allVersions.value[0]];
        });

        const cellParser = (row: IRow, column: IColumn) => {
            switch (column.key) {
                case "activationDate":
                    return {
                        template: `<div>${DateUtil.formatDate(row[column.key])}</div>`,
                    };
                default:
                    return {template: `<div><div>${TemplateHelper.escape(row[column.key])}</div></div>`};
            }
        };

        const columns = ref<IColumn[]>([
            {
                key: 'activationDate',
                label: 'Activatie datum',
                sortable: false,
                width: 10,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'pmklVersion',
                label: 'Pmkl',
                sortable: false,
                width: 5,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'wfsVersion',
                label: 'WFS',
                width: 5,
                sortable: false,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'packageVersion',
                label: 'Package',
                sortable: false,
                width: 5,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'minimumPackageVersion',
                label: 'MinPackage',
                sortable: false,
                width: 5,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'viewerVersion',
                label: 'Viewer',
                sortable: false,
                width: 5,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'minimumViewerVersion',
                label: 'MinViewer',
                sortable: false,
                width: 5,
                isVisible: true,
                parser: cellParser
            },
            {
                key: 'releaseNotes',
                label: 'ReleaseNotes',
                sortable: false,
                width: 20,
                isVisible: true,
                parser: cellParser,
            }
        ]);

        const toggleHistoryButtonText = computed(() => {
           return showAllVersions.value ? 'Verberg historiek' : 'Toon historiek';
        });

        const toggleHistory = () => {
            showAllVersions.value = !showAllVersions.value;
        };

        const addVersionToUi = () => {
            allVersions.value.unshift({
                activationDate: DateUtil.formatForApi(activationDate.value[0], 'YYYY-MM-DD 00:00:00'),
                packageVersion: klipPackage.value,
                pmklVersion: pmklVersion.value,
                wfsVersion: wfsVersion.value,
                minimumViewerVersion: minViewer.value,
                viewerVersion: viewer.value,
                minimumPackageVersion: minKlipPackage.value,
                releaseNotes: DOMPurify.sanitize(releaseNotes.value),
            });
        }

        const _saveVersion = async () => {
            savingVersions.value = true;

            const newVersion: KlipApi.IAddVersionRequest = {
                activationDate: DateUtil.formatForApi(activationDate.value[0], 'YYYY-MM-DD 00:00:00'),
                packageVersion: klipPackage.value,
                pmklVersion: pmklVersion.value,
                wfsVersion: wfsVersion.value,
                minimumViewerVersion: minViewer.value,
                viewerVersion: viewer.value,
                minimumPackageVersion: minKlipPackage.value,
                releaseNotes: DOMPurify.sanitize(releaseNotes.value),
            };

            await proxy.operations_AddVersion(KlipApi.AddVersionRequest.fromJS(newVersion));
            addVersionToUi();

            savingVersions.value = false;

            modalEvents.triggerModalToggle();
        };

        const onCreateNewVersion = () => {
            const latestVersion = first(allVersions.value);

            activationDate.value = [today];
            pmklVersion.value = latestVersion?.pmklVersion;
            wfsVersion.value = latestVersion?.wfsVersion;
            klipPackage.value = latestVersion?.packageVersion;
            minKlipPackage.value = latestVersion?.minimumPackageVersion;
            minViewer.value = latestVersion?.minimumViewerVersion;
            viewer.value = latestVersion?.viewerVersion;

            fields.releaseNotes.field.resetField();

            modalEvents.triggerModalToggle();
        }

        const _loadVersions = async () => {
            const response = await proxy.operations_GetVersions();
            if (response.isSuccessStatusCode) {
                allVersions.value = response.result;
                versionsMeta.value.totalRows = response.result.length;
                versionsMeta.value.resultsPerPage = response.result.length;
                isVersionsDataTableLoading.value = false;
            }
        };

        onMounted(async () => {
            await _loadVersions();
        });

        const onSubmit = form.handleSubmit(_saveVersion);

        return {
            isVersionsDataTableLoading,
            savingVersions,
            showAllVersions,
            versions,
            activationMinDate,

            columns,
            versionsMeta,
            toggleHistory,
            toggleHistoryButtonText,

            onCreateNewVersion,

            onSubmit,

            activationDate,
            pmklVersion,
            wfsVersion,
            klipPackage,
            minKlipPackage,
            viewer,
            minViewer,
            releaseNotes,

            fields,
        };
    }
});
